import type { AxiosInstance } from "axios";

/**
 * Downloads a document file from the broker portal API.
 * Returns the file as a Blob for in-browser rendering.
 */
export const downloadDocumentFile = async (
  downloadLink: string,
  axiosInstance: AxiosInstance,
): Promise<Blob> => {
  const response = await axiosInstance.post<Blob>(
    "brokerportal/v1/getRfpFile",
    { downloadLink },
    { responseType: "blob" },
  );

  return response.data;
};

/**
 * Opens a binary PDF in an existing browser window/tab at the given page number.
 * Returns false if the window is closed or unavailable.
 */
export const openBinaryPdfInWindow = (
  viewerWindow: Window | null,
  binaryFile: BlobPart,
  pageNumber?: number,
): boolean => {
  if (!viewerWindow || viewerWindow.closed) {
    return false;
  }

  const blob = new Blob([binaryFile], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  const pageSuffix = pageNumber && pageNumber > 0 ? `#page=${pageNumber}` : "";
  viewerWindow.location.href = `${blobUrl}${pageSuffix}`;

  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

  return true;
};

/**
 * Opens a binary PDF in a new browser tab at the given page number.
 * Returns false if the popup was blocked.
 */
export const openBinaryPdfInNewTab = (
  binaryFile: BlobPart,
  pageNumber?: number,
): boolean => {
  const viewerWindow = window.open("", "_blank");
  if (viewerWindow) {
    viewerWindow.opener = null;
  }
  return openBinaryPdfInWindow(viewerWindow, binaryFile, pageNumber);
};
